import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPricingPackages } from '../services/api';
import { whatsappUrl } from '../utils/whatsapp';
import './Packages.css';

// Agreed 30 Oct 2026: only the free package (Partial Scholarships) is free;
// every other package starts with this fee, which is part of the package
// price (not extra) and non-refundable. Shown under the package grid.
export const COMMITMENT_FEE_NOTE =
  "All other packages start with a $100 commitment fee. It is part of the package price, not an extra cost, and it can't be refunded, so we'll explain exactly what it covers before you pay.";

// price is free text on the backend ("USD$0.00"); a package is free when
// the number in it is zero.
export function isFreePackage(pkg) {
  const match = String(pkg?.price || '').match(/(\d+(?:\.\d+)?)/);
  return Boolean(match) && Number(match[1]) === 0;
}

const DEFAULT_ICON_SVG = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="package-icon">
    <path d="M12 6.25278V19.2528M12 6.25278C10.8321 5.47686 9.24649 5 7.5 5C5.75351 5 4.16789 5.47686 3 6.25278V19.2528C4.16789 18.4769 5.75351 18 7.5 18C9.24649 18 10.8321 18.4769 12 19.2528M12 6.25278C13.1679 5.47686 14.7535 5 16.5 5C18.2465 5 19.8321 5.47686 21 6.25278V19.2528C19.8321 18.4769 18.2465 18 16.5 18C14.7535 18 13.1679 18.4769 12 19.2528" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FALLBACK_PACKAGES = [
  { id: 'starter', name: 'Lite Package', price: 'USD$349.49', price_detail: 'per student', description: 'Basic educational consultation for students beginning their academic journey.', features: ['University applications & placements', 'Visa application assistance', 'Airport pickup', 'Accommodation assistance', 'University registration', 'Residence permit application'], recommended: false, color: '#10b981', icon_url: null },
  { id: 'standard', name: 'In Turkey Package', price: 'USD$399.99', price_detail: 'per student', description: 'Comprehensive guidance for students already in Turkey seeking solid academic and career planning.', features: ['Airport pickup', 'Accommodation assistance', 'University registration', 'Residence permit application assistance', 'Documents authentication in Ankara', 'Denklik/Equivalence Letter application assistance'], recommended: false, color: '#3b82f6', icon_url: null },
  { id: 'premium', name: 'Premium', price: 'USD$599.99', price_detail: 'per student', description: 'Elite consultation service with extensive support throughout the academic journey and full stack services.', features: ['University applications and placement', 'Flight booking and itinerary', 'Visa application Assistance', 'Airport pickup', 'Accommodation assistance', 'University registration', 'Residence permit application assistance', 'Documents authentication in Ankara', 'Denklik/Equivalence Letter application assistance', 'Job searching'], recommended: true, color: '#8b5cf6', icon_url: null },
];

function mapPackageToCard(pkg) {
  return {
    id: String(pkg.id),
    name: pkg.name || '',
    price: pkg.price || '',
    priceDetail: pkg.price_detail || '',
    description: pkg.description || '',
    features: Array.isArray(pkg.features) ? pkg.features : [],
    recommended: Boolean(pkg.recommended),
    color: pkg.color || '#3b82f6',
    iconUrl: pkg.icon_url || null,
  };
}

// embedded: rendered inside another page (country pages), which owns the
// page's h1 and section heading, so the header block is left out.
const Packages = ({ embedded = false }) => {
  const { pathname } = useLocation();
  const [activePackage, setActivePackage] = useState(null);
  const [isInView, setIsInView] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPricingPackages()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.results || []);
        if (list.length > 0) {
          setPackages(list.map(mapPackageToCard));
          setActivePackage((prev) => prev || String(list[0].id));
        } else {
          setPackages(FALLBACK_PACKAGES.map(mapPackageToCard));
          setActivePackage((prev) => prev || 'starter');
        }
      })
      .catch(() => {
        setPackages(FALLBACK_PACKAGES.map(mapPackageToCard));
        setActivePackage((prev) => prev || 'starter');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (packages.length > 0 && !activePackage) setActivePackage(packages[0].id);
  }, [packages, activePackage]);

  // Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.querySelector('.packages-container');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  if (loading && packages.length === 0) {
    return (
      <div className="packages-container">
        <div className="packages-wrapper">
          <div className="packages-header">
            <h1 className="sr-only">Consultation packages &amp; pricing</h1>
            <h2 className="packages-title">Our Consultation Packages</h2>
            <p className="packages-subtitle">Loading packages…</p>
          </div>
        </div>
      </div>
    );
  }

  const displayPackages = packages.length > 0 ? packages : FALLBACK_PACKAGES.map(mapPackageToCard);
  const hasFreePackage = displayPackages.some(isFreePackage);

  return (
    <div className={`packages-container ${embedded ? 'packages-container--embedded' : ''} ${isInView ? 'in-view' : ''}`}>
      <div className="packages-wrapper">
        {!embedded && (
        <div className="packages-header">
          {/* Page has no other visible title — "Our Consultation Packages"
              below is the intended visible page heading, so this h1 is
              sr-only to satisfy page-has-heading-one with zero visual
              change. Resulting hierarchy: h1 (hidden) → h2 → h3 (package
              names). */}
          <h1 className="sr-only">Consultation packages &amp; pricing</h1>
          <h2 className="packages-title">Our Consultation Packages</h2>
          <p className="packages-subtitle">
            Choose the right level of support for your educational journey with our customized consultation packages.
          </p>
          <div className="packages-divider"></div>
        </div>
        )}

        <div className="packages-grid">
          {displayPackages.map((pkg, index) => (
            <div 
              key={pkg.id}
              data-package-id={pkg.id}
              className={`package-card ${activePackage === pkg.id ? 'package-active' : ''}`}
              onMouseEnter={() => setActivePackage(pkg.id)}
              style={{ 
                '--delay': `${index * 0.1}s`,
                '--accent-color': pkg.color
              }}
            >
              <div className="package-card-inner">
                {isFreePackage(pkg) ? (
                  <div className="package-recommended-badge package-free-badge">
                    Free
                  </div>
                ) : pkg.recommended && (
                  <div className="package-recommended-badge">
                    Most Popular
                  </div>
                )}
                <div className="package-header">
                  <div className="package-icon-wrapper" style={{ backgroundColor: pkg.color + '15' }}>
                    {pkg.iconUrl ? <img src={pkg.iconUrl} alt="" className="package-icon" width={24} height={24} loading="lazy" decoding="async" /> : DEFAULT_ICON_SVG}
                  </div>
                  <h3 className="package-name">{pkg.name}</h3>
                </div>
                <div className="package-content">
                  <div className="package-price-container">
                    <div className="package-price">{isFreePackage(pkg) ? 'Free' : pkg.price}</div>
                    <div className="package-price-detail">{pkg.priceDetail}</div>
                  </div>
                  <p className="package-description">{pkg.description}</p>
                  <div className="package-features-heading">What's included:</div>
                  <ul className="package-features">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="package-feature-item">
                        <svg 
                          className="feature-check-icon" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M5 13l4 4L19 7" 
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="package-footer">
                  <a
                    href={whatsappUrl(pathname, { extra: `I'm interested in the ${pkg.name} package.` })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`package-button ${activePackage === pkg.id ? 'package-button-active' : ''}`}
                  >
                    Ask about {pkg.name} on WhatsApp
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasFreePackage && (
          <p className="packages-commitment-note">{COMMITMENT_FEE_NOTE}</p>
        )}
      </div>
    </div>
  );
};

export default Packages;